import {
  addPlayer,
  canSave,
  choosesShortCourt,
  GameSetup,
  MIN_PLAYERS,
  maxPlayers,
  removePlayer,
  selectShortCourt,
  setFormat,
  setupOf,
  shortCourtTitle,
  toggleCourt,
} from "@/game/config/gameSetup";
import { allCourts } from "@/helpers/gameHelpers";
import { useConvexConfig } from "@/hooks/useConvexConfig";
import { cn } from "@/lib/utils";
import { useGameStore } from "@/store/gameStore";
import { Check, Minus, Plus } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import {
  ChoiceButton,
  CircleButton,
  ConfirmDialog,
  PanelHeader,
} from "./panelControls";

/**
 * Court setup, shown when the card grows: format, courts, which court takes
 * the leftover players, and how many are playing. Changes only apply when
 * saved. A row that comes or goes opens in place, so the card grows at the
 * top and the players stepper at the bottom stays put.
 */
export const SetupFace: React.FC<{ close: () => void }> = ({ close }) => {
  const [setup, setSetup] = useState(() =>
    setupOf(useGameStore.getState().game),
  );
  const [confirming, setConfirming] = useState(false);
  const isPlaying = useGameStore((state) => state.isPlaying());
  const { configGameWithSync } = useConvexConfig();

  // The short court row's title, kept while the row closes so it doesn't
  // change on the way out.
  const shortTitle = useRef(shortCourtTitle(setup));
  if (choosesShortCourt(setup)) shortTitle.current = shortCourtTitle(setup);

  const change = (edit: (setup: GameSetup) => GameSetup) =>
    setSetup((current) => edit(current));

  // Closes once the new round is dealt, so the card shrinks back to show it
  // rather than an empty table.
  const start = async () => {
    setConfirming(false);
    await configGameWithSync(setup);
    close();
  };

  return (
    <div className="flex flex-col p-4 text-white">
      <div className="flex flex-col gap-5">
        <PanelHeader title="Court Setup" closeLabel="Cancel" onClose={close}>
          {/* Same round button as ✕, in liquid green with a dark check
              like Draw's text. */}
          <CircleButton
            label="Save"
            tinted
            disabled={!canSave(setup)}
            onClick={() => (isPlaying ? setConfirming(true) : start())}
          >
            <Check className="size-6" strokeWidth={3} />
          </CircleButton>
        </PanelHeader>
        <div className="flex gap-2" role="radiogroup" aria-label="Game format">
          {[
            { title: "Singles", perCourt: 2 },
            { title: "Doubles", perCourt: 4 },
          ].map(({ title, perCourt }) => (
            <ChoiceButton
              key={perCourt}
              role="radio"
              selected={setup.perCourt === perCourt}
              className="h-14 text-2xl"
              onClick={() => change((s) => setFormat(s, perCourt))}
            >
              {title}
            </ChoiceButton>
          ))}
        </div>
        <Section title={setup.courts.length ? "Courts" : "Select Courts"}>
          <CourtGrid
            label="Select courts"
            role="checkbox"
            courts={allCourts}
            centerLastRow
            isSelected={(court) => setup.courts.includes(court)}
            select={(court) => change((s) => toggleCourt(s, court))}
          />
        </Section>
      </div>
      <Reveal shown={choosesShortCourt(setup)}>
        <Section title={shortTitle.current}>
          <CourtGrid
            label="Select short court"
            role="radio"
            courts={setup.courts}
            isSelected={(court) => court === setup.shortCourt}
            select={(court) => change((s) => selectShortCourt(s, court))}
          />
        </Section>
      </Reveal>
      <Reveal shown={setup.players > 0}>
        <Players
          setup={setup}
          remove={() => change(removePlayer)}
          add={() => change(addPlayer)}
        />
      </Reveal>
      <ConfirmDialog
        open={confirming}
        title="Start New Round?"
        message="There is a round in progress. Begin a new round with these settings?"
        confirm="Start New Round"
        onConfirm={start}
        onCancel={() => {
          setConfirming(false);
          close();
        }}
      />
    </div>
  );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <div className="flex flex-col gap-3">
    <h3 className="text-center text-xl font-extrabold text-white/60 uppercase">
      {title}
    </h3>
    {children}
  </div>
);

/**
 * A row that opens by growing from no height and closes by shrinking back,
 * showing only as much of itself as fits. It stays in the layout either
 * way, so the rows around it move with the card instead of it landing on top
 * of them.
 */
const Reveal: React.FC<{ shown: boolean; children: React.ReactNode }> = ({
  shown,
  children,
}) => (
  <div
    className={cn(
      "ease-snappy grid transition-[grid-template-rows] duration-[390ms] motion-reduce:transition-none",
      shown ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
    )}
    inert={!shown}
  >
    {/* Pinned to the bottom, so the row sits still as the space above it
        opens or closes. */}
    <div className="flex min-h-0 flex-col justify-end overflow-hidden">
      {/* Fades out before the rows above slide over, and in once there's
          room. The gap above belongs to the row, so it closes too. */}
      <div
        className={cn(
          "pt-5 transition-opacity",
          shown
            ? "opacity-100 delay-100 duration-150 ease-in"
            : "opacity-0 duration-75 ease-out",
        )}
      >
        {children}
      </div>
    </div>
  </div>
);

/** Courts as a grid of toggles, five to a row. */
const CourtGrid: React.FC<{
  label: string;
  role: "radio" | "checkbox";
  courts: number[];
  centerLastRow?: boolean;
  isSelected: (court: number) => boolean;
  select: (court: number) => void;
}> = ({ label, role, courts, centerLastRow = false, isSelected, select }) => (
  <div
    className="flex flex-col gap-2"
    role={role === "radio" ? "radiogroup" : "group"}
    aria-label={label}
  >
    {rowsOf(courts, centerLastRow).map((row, index) => (
      <div key={index} className="flex gap-2">
        {row.map((court, cell) =>
          court === null ? (
            <div key={`empty-${cell}`} className="min-w-0 flex-1" />
          ) : (
            <ChoiceButton
              key={court}
              role={role}
              selected={isSelected(court)}
              className="h-15 text-3xl"
              onClick={() => select(court)}
            >
              {court}
            </ChoiceButton>
          ),
        )}
      </div>
    ))}
  </div>
);

const COLUMNS = 5;

/** Courts in rows of five. A short last row is padded with empty cells,
 * split evenly on both sides when centered. */
const rowsOf = (courts: number[], centerLastRow: boolean) => {
  const rows: (number | null)[][] = [];
  for (let start = 0; start < courts.length; start += COLUMNS) {
    const row: (number | null)[] = courts.slice(start, start + COLUMNS);
    const missing = COLUMNS - row.length;
    if (missing > 0 && courts.length > COLUMNS) {
      const before = centerLastRow ? Math.floor(missing / 2) : 0;
      row.unshift(...Array(before).fill(null));
      row.push(...Array(missing - before).fill(null));
    }
    rows.push(row);
  }
  return rows;
};

const Players: React.FC<{
  setup: GameSetup;
  remove: () => void;
  add: () => void;
}> = ({ setup, remove, add }) => (
  <div className="flex items-center">
    <CircleButton
      label="Remove a player"
      className="h-14 w-20"
      disabled={setup.players <= MIN_PLAYERS}
      onClick={remove}
    >
      <Minus className="size-7" strokeWidth={3.5} />
    </CircleButton>
    <div className="text-accent flex flex-1 items-baseline justify-center gap-1.5 text-[22px] font-bold">
      <Count value={setup.players} />
      Players
    </div>
    <CircleButton
      label="Add a player"
      className="h-14 w-20"
      disabled={setup.players >= maxPlayers(setup)}
      onClick={add}
    >
      <Plus className="size-7" strokeWidth={3.5} />
    </CircleButton>
  </div>
);

/** A number that slides in from below as it goes up, and from above as it
 * goes down, like the court card's count. */
const Count: React.FC<{ value: number }> = ({ value }) => {
  const previous = useRef(value);
  const up = value >= previous.current;
  useEffect(() => {
    previous.current = value;
  }, [value]);

  return (
    <span
      key={value}
      className={cn(
        "animate-in fade-in inline-block tabular-nums duration-200",
        up ? "slide-in-from-bottom-3" : "slide-in-from-top-3",
      )}
    >
      {value}
    </span>
  );
};
